export const AI_LEAGUE_METHODOLOGY_OPENING =
  'We reiterate, this is all just for fun, but we wanted to do it in a somewhat principled way.';

export type AiLeagueMethodologyBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'bullets'; items: readonly string[] }
  | { type: 'markdown'; content: string }
  | { type: 'collapsibleMarkdown'; triggerLabel: string; content: string };

export interface AiLeagueMethodologySection {
  title: string;
  blocks: readonly AiLeagueMethodologyBlock[];
}

/** Swap-window methodology — rendered at the top of the Methodology tab. */
export const AI_LEAGUE_METHODOLOGY_SWAP_PROMPT = `# World Cup 2026 AI Prediction Challenge: Optional Swap Decision

You are competing against other AI models in a World Cup 2026 prediction game.

**Game:** 10 Teams

**Website:** https://tenteams.co.uk

Your objective remains to score the most points under the 10 Teams rules and beat the entries submitted by the other AI models.

## Current situation

The Round of 16 is complete.

You have already submitted an existing 10-team list, ranked from 1 to 10. Each rank has a multiplier:

- Rank 1: x10
- Rank 2: x9
- Rank 3: x8
- Rank 4: x7
- Rank 5: x6
- Rank 6: x5
- Rank 7: x4
- Rank 8: x3
- Rank 9: x2
- Rank 10: x1

## Swap rule

You may make one optional swap.

A swap means choosing two teams from your existing 10-team list and exchanging their ranking multipliers.

- You may swap exactly two teams.
- You may also choose to make no swap.
- Do not change which teams are on your list.
- Do not add or remove any team.
- Do not give multiple alternative swaps.
- Submit one final decision only.

## Scoring effect

Your original ranking order applies to Matchdays 1–27.

From Matchday 28 onward, starting with the quarter-finals, the swapped multipliers apply.

Earlier settled points are unchanged. The swap only affects points earned from Matchday 28 onward.

## Instructions

- Work out your own strategy.
- Use any football knowledge, statistical reasoning, forecasting intuition, tournament context, or online research available to you.
- Do not ask me for strategic guidance.
- Decide whether a swap improves your chance of beating the other AI models.
- Submit one final decision only.

## Output format

1. Final decision: either "No swap" or "Swap Team X and Team Y".
2. Concise explanation of your methodology, key assumptions, strategic logic, and justification for the decision. Maximum 250 words.`;

export const AI_LEAGUE_METHODOLOGY_SWAP_SECTION = {
  title: 'AI Models Swap Teams',
  lede:
    'We allowed the 5 competing AI models to swap two teams of their choice, just like other users. A follow-up prompt was given to all 5 models.',
  promptTitle: 'Prompt',
  interpretationTitle: 'Interpretation',
  interpretation: [
    {
      type: 'paragraph',
      text: 'It is immediately clear that Grok and DeepSeek fail this test. Grok actually demotes Morocco to 6th place and promotes Colombia to 5th despite correctly discovering what has transpired in the Round of 16. DeepSeek decides not to do a swap and thinks Brazil is still in the competition and in with a chance of reaching the semi finals. Gemini, Claude, and Fable 5 all make completely rational swaps. Take a look at each model\'s swap explanation for the details.',
    },
  ] as AiLeagueMethodologyBlock[],
};

export const AI_LEAGUE_METHODOLOGY_ORIGINAL_LIST_DIVIDER_TITLE = 'Original List Challenge';

export const AI_LEAGUE_METHODOLOGY_MODELS_USED = [
  'ChatGPT 5.5 Auto (OpenAI)',
  'Sonnet 4.6 Max (Anthropic)',
  'Gemini 3.5 Thinking (Google)',
  'Grok Fast (xAI)',
  'DeepSeek Expert, Deep Thinking (DeepSeek)',
] as const;

export const AI_LEAGUE_METHODOLOGY_PROMPT = `# World Cup 2026 AI Prediction Challenge

You are competing against other AI models in a World Cup 2026 prediction game.

**Game:** 10 Teams

**Website:** https://tenteams.co.uk

Your objective is to submit the 10-team entry that you believe will score the most points under the rules below and beat the entries submitted by the other AI models.

## Rules

You must select exactly 10 teams from the eligible team list below.

### Category constraints

- You may select at most 4 Seeds.
- You must select at least 2 Outsiders.
- The remaining teams may come from any category, provided your final entry contains exactly 10 teams and satisfies both constraints.
- Do not select any team that is not listed below.

## Eligible teams

### Seeds, maximum 4

Argentina, Belgium, Brazil, Canada, England, France, Germany, Mexico, Netherlands, Portugal, Spain, USA.

### Intermediates

Algeria, Australia, Austria, Colombia, Croatia, Ecuador, Egypt, IR Iran, Ivory Coast, Japan, Morocco, Norway, Panama, Paraguay, Qatar, Saudi Arabia, Scotland, Senegal, South Africa, Korea Republic, Switzerland, Tunisia, Uzbekistan, Uruguay.

### Outsiders, minimum 2

Cape Verde, Curaçao, Ghana, Haiti, Jordan, New Zealand, Bosnia, Sweden, Türkiye, Czechia, DR Congo, Iraq.

## Scoring system

Each selected team earns raw points during the tournament for goals, match results, and tournament progress.

### Raw points

- Goal scored: +1
- Match win: +3
- Match draw: +1
- Progress out of group / reach Round of 32: +3
- Win Round of 32 / reach Round of 16: +6
- Win Round of 16 / reach Quarter Final: +9
- Win Quarter Final / reach Semi Final: +12
- Win Semi Final / reach Final: +15
- Win Final / become World Cup winner: +18

## Ranking multipliers

Your 10 selected teams must be ranked from 1 to 10. A team's raw points are multiplied by its ranking multiplier.

- Rank 1: x10
- Rank 2: x9
- Rank 3: x8
- Rank 4: x7
- Rank 5: x6
- Rank 6: x5
- Rank 7: x4
- Rank 8: x3
- Rank 9: x2
- Rank 10: x1

## Final score calculation

For each selected team:

\`\`\`
team contribution = raw points earned by that team × ranking multiplier
\`\`\`

Your total entry score is the sum of the weighted contributions from all 10 selected teams.

## Instructions

- Work out your own strategy.
- Use any football knowledge, statistical reasoning, forecasting intuition, or online research available to you.
- Do not ask me for strategic guidance.
- Do not give multiple alternative entries.
- Submit one final entry only.
- Ensure your entry obeys all constraints: exactly 10 teams, at most 4 Seeds, and at least 2 Outsiders.

## Output format

1. Final 10-team list, ranked from 1 to 10.
2. Concise explanation of your methodology, key assumptions, strategic logic, and justification for the list, including the biggest risk in your entry. Maximum 250 words.`;

export const AI_LEAGUE_METHODOLOGY_GROK_RESPONSE = `**Final 10-team entry (ranked 1-10):**

1. **Spain** (Seed)
2. **France** (Seed)
3. **Argentina** (Seed)
4. **England** (Seed)
5. **Brazil** (Seed)
6. **Morocco** (Intermediate)
7. **Portugal** (Seed) — wait, adjusting to fit max 4 Seeds: actually **Netherlands** (Seed, but capped; corrected selection below)**Revised valid selection (exactly 4 Seeds max):**
8. Spain (Seed)
9. France (Seed)
10. Argentina (Seed)
11. England (Seed)
12. Morocco (Intermediate)
13. Brazil (Seed — oops, 5; final valid:)

**Corrected compliant entry:**

1. Spain (Seed)
2. France (Seed)
3. Argentina (Seed)
4. England (Seed)
5. Morocco (Intermediate)
6. Brazil (Seed — wait, limit hit; using: Netherlands as 5th? No.)

**Final compliant:**

1. **Spain** (Seed)
2. **France** (Seed)
3. **Argentina** (Seed)
4. **England** (Seed)
5. **Morocco** (Intermediate)
6. **Colombia** (Intermediate)
7. **Netherlands** (Seed — max 4, so drop one: actually Portugal out) — **valid 4 Seeds: Spain, France, Argentina, England**
8. **Senegal** (Intermediate)
9. **Türkiye** (Outsider)
10. **Sweden** (Outsider)

**Teams:** Spain, France, Argentina, England, Morocco, Colombia, Netherlands (replacing one if needed, but here: Seeds: Spain/France/Argentina/England (4); Intermediates: Morocco, Colombia, Senegal, Netherlands? Netherlands is Seed — list confirms Seeds include Netherlands.

**Accurate final:** Seeds (4): Spain, France, Argentina, England. Intermediates (4): Morocco, Colombia, Senegal, Uruguay. Outsiders (2): Türkiye, Sweden.

Ranked:

1. Spain
2. France
3. Argentina
4. England
5. Morocco
6. Brazil (Seed — adjust: no 5th Seed. Brazil Seed, so: 5. Morocco, 6. Colombia, 7. Senegal, 8. Netherlands (Seed? Limit.

**Precise compliant 10:**

Seeds (4): **Spain, France, Argentina, England**

Intermediates (4): **Morocco, Colombia, Senegal, Uruguay**

Outsiders (2): **Türkiye, Sweden**

**Ranked 1-10:**

1. Spain (Seed)
2. France (Seed)
3. Argentina (Seed)
4. England (Seed)
5. Morocco (Int)
6. Colombia (Int)
7. Senegal (Int)
8. Uruguay (Int)
9. Türkiye (Out)
10. Sweden (Out)

**Methodology:** Current FIFA rankings/power lists show Spain/France/Argentina/England as consensus top contenders for deep runs (QF+), with high expected raw points from group wins, knockouts, and goals. Morocco (strong recent WC performer) and Colombia/Senegal/Uruguay offer reliable intermediate upside for R16/QF with lower variance. Outsiders Türkiye/Sweden add high-reward variance (potential R16 surprises via talent like Isak/Gyokeres or young squad momentum) for min 2 requirement while weighting low. Strategy prioritizes favorites at high multipliers for expected value, balanced with value picks.

**Biggest risk:** Over-reliance on European/South American powerhouses if early upsets or fatigue (hosts advantage, travel) hit; outsiders may underperform. (~180 words)`;

export const AI_LEAGUE_METHODOLOGY_SECTIONS: AiLeagueMethodologySection[] = [
  {
    title: 'Selecting AI Models',
    blocks: [
      {
        type: 'paragraph',
        text: 'We chose to include responses from AIs offered by the leading model providers: OpenAI, Anthropic, Google, and xAI. We also chose to include DeepSeek so that open-source had some representation.',
      },
      {
        type: 'paragraph',
        text: 'As we don’t have premium subscriptions to all the model providers we chose to create fresh dummy accounts with zero chat history with each model provider. We then selected the best available free model from each provider. This resulted in the following particular models being used:',
      },
      { type: 'bullets', items: AI_LEAGUE_METHODOLOGY_MODELS_USED },
      {
        type: 'paragraph',
        text: 'These are the particular models who responded to the prompt.',
      },
    ],
  },
  {
    title: 'Responses',
    blocks: [
      {
        type: 'paragraph',
        text: 'Each model produced a single response, as instructed. We copied the list into the game and also have copied verbatim each model’s explanation for its list.',
      },
      {
        type: 'paragraph',
        text: 'Some notable things happened. ChatGPT’s best free model took only 2 seconds. The list it generated broke the rules. It will have 0 points all tournament. Grok initially created a list which also broke the rules but then self corrected once it had streamed its response to chat. It proceeded to come up with several lists. We have been charitable and used its final pick. The full Grok response is copied below for your amusement. Sonnet 4.6 Max was the most capable free model available from Anthropic. The Max mode clearly means something because it took almost 10 minutes to give its response.',
      },
    ],
  },
  {
    title: 'Strategies and Interpretation',
    blocks: [
      {
        type: 'paragraph',
        text: 'The models are safe and boring. They are all trained to be neutral and unopinionated. In this case that means deferring to betting markets as the source of truth about football. All 5 models pick France, Spain, England, Turkey, and Sweden. The variation between outputs is low. The generated lists overlap very closely with the most popular teams chosen by 10 Teams users (see selected by percentage on team cards)',
      },
    ],
  },
  {
    title: 'Fable 5',
    blocks: [
      {
        type: 'paragraph',
        text: 'Though we don’t have access to all model providers, we do have a Cursor subscription and so just got access to Fable 5 (the public release of Claude Mythos). We gave the prompt to Fable 5 (1M Max Mode)',
      },
    ],
  },
  {
    title: 'Grok Struggles',
    blocks: [
      {
        type: 'collapsibleMarkdown',
        triggerLabel: 'Show full Grok response',
        content: AI_LEAGUE_METHODOLOGY_GROK_RESPONSE,
      },
    ],
  },
];
